// Search + status + channel + date-range filters for the campaigns list.
// Search input is debounced locally and dispatched upward via onChange.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { SearchBar, SearchSelect } from '../../../../ui';
import { media } from '../../../../utils/media';
import { CAMPAIGN_STATUSES, CHANNELS, LIMITS } from '../../constants';

const Bar = styled.div`
  display: grid;
  grid-template-columns: 1fr 180px 180px 180px;
  gap: 12px;
  align-items: center;
  padding: 12px;
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  margin-bottom: 16px;

  ${media.lessThan('tablet')} {
    grid-template-columns: 1fr 1fr;
  }
  ${media.lessThan('phablet')} {
    grid-template-columns: 1fr;
  }
`;

// Sentinel value for the "all" option in each dropdown. Selecting it clears
// the corresponding filter back to null in the upstream filter state.
const ALL = '__all';

const DATE_PRESETS = [
  { id: 'all', name: 'All time' },
  { id: 'thisMonth', name: 'This month' },
  { id: 'last30', name: 'Last 30 days' },
  { id: 'last90', name: 'Last 90 days' },
  { id: 'thisYear', name: 'This year' },
];

const toOption = (id, label) => ({ id, name: label });
const findOption = (options, id) => options.find((o) => o.id === id) ?? options[0];

const CampaignsFilterBar = ({ value, onChange }) => {
  const { t } = useTranslation();
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const [localSearch, setLocalSearch] = useState(value?.search ?? '');

  useEffect(() => setLocalSearch(value?.search ?? ''), [value?.search]);

  const statusOptions = useMemo(
    () => [
      toOption(ALL, t('campaigns.allStatuses', 'All statuses')),
      ...CAMPAIGN_STATUSES.map((s) => toOption(s, t(`campaigns.status.${s}`, s))),
    ],
    [t],
  );
  const channelOptions = useMemo(
    () => [
      toOption(ALL, t('campaigns.allChannels', 'All channels')),
      toOption('email', t('campaigns.channel.email', 'Email')),
      toOption('network-upsell', t('campaigns.channel.networkUpsell', 'In-app')),
    ],
    [t],
  );

  const handleSearch = useCallback((e) => {
    const next = e.target.value;
    setLocalSearch(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (onChange) onChange({ search: next });
    }, LIMITS.SEARCH_DEBOUNCE_MS);
  }, [onChange]);

  const handleClear = useCallback(() => {
    setLocalSearch('');
    if (searchRef.current) searchRef.current.value = '';
    if (onChange) onChange({ search: '' });
  }, [onChange]);

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  return (
    <Bar>
      <SearchBar
        placeholder={t('campaigns.searchPlaceholder', 'Search campaigns by name…')}
        handleSearch={handleSearch}
        handleClear={handleClear}
        inputRef={searchRef}
      />
      <SearchSelect
        id='campaigns-filter-status'
        options={statusOptions}
        selectedOptions={findOption(statusOptions, value?.status ?? ALL)}
        setValue={(opt) => onChange?.({ status: opt?.id && opt.id !== ALL ? opt.id : null })}
      />
      <SearchSelect
        id='campaigns-filter-channel'
        options={channelOptions}
        selectedOptions={findOption(channelOptions, value?.channel ?? ALL)}
        setValue={(opt) => onChange?.({ channel: opt?.id && opt.id !== ALL ? opt.id : null })}
      />
      <SearchSelect
        id='campaigns-filter-date'
        options={DATE_PRESETS}
        selectedOptions={findOption(DATE_PRESETS, value?.dateRange?.preset ?? 'all')}
        setValue={(opt) => onChange?.({ dateRange: { preset: opt?.id ?? 'all', from: null, to: null } })}
      />
    </Bar>
  );
};

export default CampaignsFilterBar;
export { CHANNELS };
